import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { ModelCard } from '../components/ModelCard';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronLeft, Lock, Package } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../lib/api';

export function CategoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [category, setCategory] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategory();
  }, [id]);

  const loadCategory = async () => {
    setLoading(true);
    try {
      const [catsRes, modelsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/models?category=${id}&limit=100`)
      ]);
      const found = catsRes.data.find((c) => c.id === id);
      setCategory(found || null);
      setModels(modelsRes.data);
    } catch (error) {
      console.error('Failed to load category:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-pulse text-[#0066FF] text-xl">Carregando...</div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="mt-16 pt-12 max-w-7xl mx-auto px-4 text-center py-20">
          <h1 className="text-3xl font-bold text-white mb-2">Categoria não encontrada</h1>
          <button
            onClick={() => navigate('/')}
            className="mt-4 inline-flex items-center space-x-2 bg-[#0066FF] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#3385FF] transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Voltar para o início</span>
          </button>
        </div>
      </div>
    );
  }

  const isPremiumLocked = category.premium && !user?.is_premium;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <main data-testid="category-page" className="mt-16 pt-12 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <button
              data-testid="back-button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center space-x-1 text-zinc-400 hover:text-white transition-colors mb-4 text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Voltar</span>
            </button>

            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  {category.premium ? (
                    <Lock className="w-7 h-7 text-[#00FF99]" />
                  ) : (
                    <Package className="w-7 h-7 text-[#0066FF]" />
                  )}
                  <h1 data-testid="category-title" className="text-4xl sm:text-5xl font-black text-white tracking-tight">
                    {category.name}
                  </h1>
                  {category.premium && (
                    <span className="bg-[#00FF99]/20 text-[#00FF99] text-xs font-bold px-3 py-1 rounded-full">
                      PREMIUM
                    </span>
                  )}
                </div>
                <p className="text-zinc-400">
                  {models.length} {models.length === 1 ? 'modelo encontrado' : 'modelos encontrados'}
                </p>
              </div>
            </div>
          </motion.div>

          {isPremiumLocked && (
            <div className="backdrop-blur-xl bg-gradient-to-br from-[#0066FF]/10 to-[#00FF99]/10 border border-[#00FF99]/30 rounded-xl p-6 mb-8">
              <div className="flex items-start space-x-3">
                <Lock className="w-6 h-6 text-[#00FF99] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <h3 className="text-white font-bold mb-1">Categoria Premium</h3>
                  <p className="text-zinc-300 text-sm mb-4">
                    Os modelos completos desta categoria estão disponíveis apenas para usuários Premium.
                    Você pode ver as prévias, mas precisa do acesso Premium para visualizar os detalhes.
                  </p>
                  <button
                    onClick={() => toast.info('🚀 Recurso Premium estará disponível em breve!', { duration: 4000 })}
                    className="bg-gradient-to-r from-[#0066FF] to-[#00FF99] text-black px-6 py-2.5 rounded-lg font-bold hover:opacity-90 transition-all"
                  >
                    Desbloquear Premium
                  </button>
                </div>
              </div>
            </div>
          )}

          {models.length === 0 ? (
            <div data-testid="empty-state" className="text-center py-20">
              <Package className="w-20 h-20 text-zinc-700 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Nenhum modelo nesta categoria</h2>
              <p className="text-zinc-400">Em breve teremos novos modelos por aqui</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {models.map((model) => (
                <ModelCard key={model.id} model={model} isPremiumUser={user?.is_premium} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
